/**
 * `/pedidos/$id` — a ficha do pedido.
 *
 * Duas coisas alem da tela:
 *
 * 1. **O codigo de retirada tem rota propria** (`GET /v1/me/orders/{id}/pickup-code`,
 *    `no-store` — `API §6.14`) e **nao** vem no `OrderDetail`. Ele e buscado sob demanda, por
 *    toque, e nao entra em cache nenhum. Ver a DUVIDA no cabecalho de `telas/Pedidos.tsx`.
 * 2. **Esta e a superficie do convite de instalacao** (`PWA §3.2`): "card inline na tela de
 *    'pedido confirmado'". O convite so aparece se `decidirConvite()` disser que sim — as seis
 *    condicoes do documento, avaliadas fora daqui.
 */
import { useApiClient, useApiQueries } from '@farmacia/api-client/query';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import type { ReactNode } from 'react';

import { ConviteDeInstalacao } from '../app/shell/ConviteDeInstalacao.jsx';
import { usePwa } from '../pwa/contexto.jsx';
import { useInstalacao } from '../pwa/useInstalacao.js';
import { Pedido } from '../telas/Pedidos.jsx';

export const Route = createFileRoute('/pedidos/$id')({ component: TelaDePedido });

/**
 * DUVIDA: `GET /v1/me/orders/{id}/pickup-code` esta em `API §6.14` mas nao tem metodo no
 * `@farmacia/api-client` nem schema em `@farmacia/contracts`. Pelo mesmo criterio de
 * `app/home.ts`: a chamada passa pelo `request` do cliente (ADR-0001), e o schema fica local e
 * provisorio, com o nome do campo tal como o documento o escreve.
 */
const CodigoDeRetiradaSchema = z.object({
  code: z.string().min(1),
  expiresAt: z.string(),
});

function TelaDePedido(): ReactNode {
  const { id } = Route.useParams();
  const cliente = useApiClient();
  const queries = useApiQueries();
  const { conexao } = usePwa();

  const consulta = useQuery(queries.orders.detail(id));

  /**
   * `enabled: false` — o codigo **so** e buscado por toque.
   *
   * `staleTime`/`gcTime` zero: ele e `no-store` no contrato, e mante-lo em memoria depois que a
   * tela fecha e a metade do problema que a rota propria existe para evitar.
   */
  const codigo = useQuery({
    queryKey: ['orders', 'pickup-code', id],
    queryFn: async ({ signal }) => {
      const { data } = await cliente.request({
        method: 'GET',
        path: `/v1/me/orders/${encodeURIComponent(id)}/pickup-code`,
        response: CodigoDeRetiradaSchema,
        signal,
      });
      return data;
    },
    enabled: false,
    staleTime: 0,
    gcTime: 0,
  });

  const instalacao = useInstalacao({
    rotaAtual: `/pedidos/${id}`,
    temPedidoConcluido: consulta.data !== undefined,
    indiceDaSessao: 1,
    fichasVistas: 0,
  });

  return (
    <>
      <Pedido
        pedido={consulta.data}
        carregando={consulta.isPending}
        erro={consulta.error}
        aoTentarDeNovo={() => void consulta.refetch()}
        codigoDeRetirada={codigo.data?.code ?? null}
        aoBuscarCodigo={() => void codigo.refetch()}
        buscandoCodigo={codigo.isFetching}
        offline={conexao === 'OFFLINE'}
      />

      {instalacao.decisao.convidar ? (
        <ConviteDeInstalacao
          ios={instalacao.ios}
          {...(instalacao.instalar === undefined ? {} : { aoInstalar: instalacao.instalar })}
          aoRecusar={instalacao.recusar}
        />
      ) : null}
    </>
  );
}
